/**
 * billingView — pure presentation helpers for My Account → Billing (BillingTab.vue).
 *
 * Keep this file IMPORT-FREE: cypress/node-tests/billing-tab.test.js executes this exact file (stripping `export`),
 * the same convention as src/utils/phiMask.js. The server (myaccount-service billing-summary) sends only
 * customer-safe fields; nothing here ever sees a Stripe id or an internal cost.
 *
 * Customer-facing copy lives here so it is tested in one place: no tech jargon, no "Stripe object" words,
 * no promise of anything the tab does not do (phase 1 = see plan, update card / billing details, get invoices;
 * NO cancel and NO plan change — those stay a conversation with Teamio).
 */

const FALLBACK_TZ = 'America/New_York';

/** 46000, 'usd' → "$460.00". Null / non-finite → "—" (never "NaN", never "$0.00" for an unknown amount). */
export function formatMoney(cents, currency) {
  if (cents === null || cents === undefined || !Number.isFinite(Number(cents))) return '—';
  const code = String(currency || 'usd').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(Number(cents) / 100);
  } catch (e) {
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }
}

/** "month" → "month", "year" → "year"; anything else → "" so a bad value never prints "undefined". */
export function intervalLabel(interval) {
  return interval === 'month' || interval === 'year' ? interval : '';
}

/**
 * ISO instant → "Nov 14, 2026" in the PRACTICE's timezone (Documents/CLAUDE.md: timestamps are shown in the site's
 * timezone). A bad timezone falls back instead of throwing; a bad date → "—".
 */
export function formatBillingDate(iso, timeZone) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const opts = { year: 'numeric', month: 'short', day: 'numeric' };
  try {
    return d.toLocaleDateString('en-US', { ...opts, timeZone: timeZone || FALLBACK_TZ });
  } catch (e) {
    return d.toLocaleDateString('en-US', { ...opts, timeZone: FALLBACK_TZ });
  }
}

const BRAND_NAMES = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover', diners: 'Diners Club', jcb: 'JCB', unionpay: 'UnionPay' };

export function brandName(brand) {
  const b = String(brand || '').toLowerCase();
  return BRAND_NAMES[b] || 'Card';
}

/** { brand:'visa', last4:'4242' } → "Visa ending 4242". */
export function cardLabel(card) {
  if (!card) return '';
  return `${brandName(card.brand)} ending ${card.last4 || '••••'}`;
}

/** { expMonth:12, expYear:2030 } → "12/2030". */
export function cardExpiry(card) {
  if (!card || !card.expMonth || !card.expYear) return '';
  return `${String(card.expMonth).padStart(2, '0')}/${card.expYear}`;
}

/**
 * The warning shown above the card. `null` when there is nothing to warn about.
 * variant is a Bootstrap alert variant. An expired card is the urgent case — the next payment will not go through.
 */
export function cardBanner(card) {
  if (!card) return null;
  if (card.status === 'expired') {
    return { variant: 'danger', text: `Your ${cardLabel(card)} expired on ${cardExpiry(card)}. Please update your card so your next payment goes through.` };
  }
  if (card.status === 'expiring') {
    return { variant: 'warning', text: `Your ${cardLabel(card)} expires ${cardExpiry(card)}. Please update it before then so your payments keep going through.` };
  }
  return null;
}

/** Sum of the line items — shown only as "plan total" when the server gave no preview total. */
export function itemsTotalCents(plan) {
  return ((plan && plan.items) || []).reduce((sum, it) => sum + (Number(it.amountCents) || 0), 0);
}

/**
 * What the screen should show:
 *   'no-billing'  the practice has no billing account yet            → "contact us"
 *   'no-plan'     a billing account but no live subscription         → card + invoices still shown
 *   'ready'       at least one live plan
 */
export function billingState(summary) {
  if (!summary || !summary.hasBilling) return 'no-billing';
  return (summary.plans || []).length ? 'ready' : 'no-plan';
}

const STATUS = {
  paid: { label: 'Paid', tone: 'ok' },
  open: { label: 'Payment due', tone: 'warn' },
  uncollectible: { label: 'Unpaid', tone: 'bad' },
};

export function invoiceStatus(status) {
  return STATUS[status] || { label: 'Processing', tone: 'neutral' };
}

/** Plan status shown next to the plan (active is the normal case and shows nothing alarming). */
export function planStatus(status) {
  if (status === 'past_due' || status === 'unpaid') return { label: 'Payment past due', tone: 'bad' };
  if (status === 'trialing') return { label: 'Free trial', tone: 'ok' };
  return { label: 'Active', tone: 'ok' };
}

/**
 * Turn any failure from the billing endpoints into ONE customer-safe sentence. The server returns error bodies as a bare
 * JSON string (common.createErrorResponse), so `err.response.data` is a string, not an object — handle both.
 */
export function billingErrorMessage(err) {
  const status = err && err.response && err.response.status;
  const data = err && err.response && err.response.data;
  const fromServer = typeof data === 'string' ? data : (data && (data.message || data.error)) || '';
  if (status === 403) return 'Billing is available to your practice’s Admins only.';
  if (status === 401) return 'Your session could not be confirmed. Please reload the page and try again.';
  if (status === 503) return fromServer || 'Billing is not available right now. Please contact Teamio support.';
  if (fromServer && status && status < 500) return fromServer;
  return (err && err.noToken && err.message) || 'We could not load your billing details right now. Please try again in a moment.';
}
